/**
 * Secure form submission hook with rate limiting and spam protection
 * Uses edge function to bypass direct database access
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LeadData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  message?: string;
  source?: string;
  language?: string;
  gdpr_consent?: boolean;
}

export interface OrderData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  message?: string;
  serviceId: string;
  serviceName: string;
  basePrice: number;
  totalPrice: number;
  selectedAddons?: string[];
  languages?: string[];
  channels?: string[];
  workingHours?: string;
  agentCount?: number;
}

interface SubmitResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  retryAfter?: number;
}

export function useSecureFormSubmit() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitLead = async (data: LeadData): Promise<SubmitResult<{ id: string }>> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('submit-form', {
        body: {
          type: 'lead',
          data,
          honeypot: '', // Empty honeypot field
        },
      });

      if (fnError) {
        const errorMessage = fnError.message || 'Failed to submit form';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      if (result?.error) {
        setError(result.error);
        return { 
          success: false, 
          error: result.error,
          retryAfter: result.retryAfter,
        };
      }

      return { success: true, data: result.data };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitOrder = async (data: OrderData): Promise<SubmitResult<{ id: string; orderNumber: string }>> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('submit-form', {
        body: {
          type: 'order',
          data,
          honeypot: '', // Empty honeypot field
        },
      });

      if (fnError) {
        const errorMessage = fnError.message || 'Failed to submit order';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      if (result?.error) {
        setError(result.error);
        return { 
          success: false, 
          error: result.error,
          retryAfter: result.retryAfter,
        };
      }

      return { success: true, data: result.data };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitLead,
    submitOrder,
    isSubmitting,
    error,
    clearError: () => setError(null),
  };
}
